export interface BundleVerification {
  loads: boolean;
  exposes_global: boolean;
  implements_init: boolean;
  has_destroy: boolean;
  error?: string;
  checked_at: string;
}

const TIMEOUT_MS = 15_000;

/** Escapes a value for embedding as a JS string literal inside an HTML <script>. */
function jsString(value: string): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

/**
 * The harness runs inside a sandboxed iframe with no allow-same-origin, so the
 * bundle evaluates in an opaque origin (`Origin: null`) with no access to the
 * portal's DOM or storage — it cannot read cookies, localStorage, or anything
 * else in this document. It can still issue cross-origin requests with
 * `credentials: "include"`; the server is responsible for refusing the
 * resulting `Origin: null` so those requests are never fulfilled with the
 * admin's session (see the CORS config in platform-api's server.ts).
 */
function buildHarness(url: string, nonce: string): string {
  return `<!doctype html><html><body><script>
(function () {
  var nonce = ${jsString(nonce)};
  var url = ${jsString(url)};
  function report(result) {
    result.nonce = nonce;
    parent.postMessage(result, "*");
  }
  window.addEventListener("error", function (e) {
    report({
      loads: true,
      exposes_global: false,
      implements_init: false,
      has_destroy: false,
      error: String((e && e.message) || "The bundle threw while evaluating")
    });
  });
  var s = document.createElement("script");
  s.src = url;
  s.onload = function () {
    var bundle = window.teamtableContent;
    var present = Boolean(bundle) && typeof bundle === "object";
    report({
      loads: true,
      exposes_global: present,
      implements_init: present && typeof bundle.init === "function",
      has_destroy: present && typeof bundle.destroy === "function",
      error: present ? undefined : "The bundle loaded but never set window.teamtableContent"
    });
  };
  s.onerror = function () {
    report({
      loads: false,
      exposes_global: false,
      implements_init: false,
      has_destroy: false,
      error: "The browser could not load a script from that URL"
    });
  };
  document.head.appendChild(s);
})();
</script></body></html>`;
}

/**
 * Loads a candidate bundle in a throwaway sandboxed frame and reports whether it
 * satisfies the TeamtableContentBundle contract. The first message wins — a
 * bundle that throws while evaluating reports through the error listener before
 * onload fires.
 */
export function verifyBundleInBrowser(url: string): Promise<BundleVerification> {
  return new Promise((resolve) => {
    const nonce = crypto.randomUUID();
    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-scripts");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.display = "none";

    let settled = false;

    const finish = (result: BundleVerification) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      iframe.remove();
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data as Record<string, unknown> | null;
      if (!data || typeof data !== "object" || data.nonce !== nonce) return;
      finish({
        loads: Boolean(data.loads),
        exposes_global: Boolean(data.exposes_global),
        implements_init: Boolean(data.implements_init),
        has_destroy: Boolean(data.has_destroy),
        error: typeof data.error === "string" ? data.error : undefined,
        checked_at: new Date().toISOString(),
      });
    };

    const timer = setTimeout(() => {
      finish({
        loads: false,
        exposes_global: false,
        implements_init: false,
        has_destroy: false,
        error: `No response after ${TIMEOUT_MS / 1000}s — the URL may be unreachable from this browser`,
        checked_at: new Date().toISOString(),
      });
    }, TIMEOUT_MS);

    window.addEventListener("message", onMessage);
    iframe.srcdoc = buildHarness(url, nonce);
    document.body.appendChild(iframe);
  });
}
